import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import type { StudioId } from '@/domain/types';
import { dayKeyOf, targetWeek, daysInRange } from '@/domain/reportingEngine';
import { buildReportingScope } from '@/domain/reportingCompare';
import { reportingMoneyTabOnly } from '@/domain/permissions';
import ReportDateFilter from '@/components/reporting/ReportDateFilter';
import StudioCompareFilter from '@/components/reporting/StudioCompareFilter';
import SummaryTab from '@/screens/reporting/leak/SummaryTab';
import LeakTab from '@/screens/reporting/leak/LeakTab';
import TargetsTab from '@/screens/reporting/leak/TargetsTab';
import RebooksTab from '@/screens/reporting/leak/RebooksTab';
import LeadsAgentsTab from '@/screens/reporting/leak/LeadsAgentsTab';
import CustomersFunnelTab from '@/screens/reporting/leak/CustomersFunnelTab';
import MoneyTab from '@/screens/reporting/leak/MoneyTab';
import OwnerTab from '@/screens/reporting/leak/OwnerTab';
import LeaderboardTab from '@/screens/reporting/leak/LeaderboardTab';
import ReportingTabBoundary from '@/components/reporting/ReportingTabBoundary';

const TABS = [
  { key: 'summary', label: 'Summary', ownerOnly: false },
  { key: 'leak', label: 'The Leak', ownerOnly: false },
  { key: 'targets', label: 'Targets', ownerOnly: false },
  { key: 'rebooks', label: 'Rebooks', ownerOnly: false },
  { key: 'leads', label: 'Leads & agents', ownerOnly: false },
  { key: 'customers', label: 'Customers & funnel', ownerOnly: false },
  { key: 'money', label: 'Money', ownerOnly: true },
  { key: 'owner', label: 'Owner', ownerOnly: true },
  { key: 'leaderboard', label: 'Leaderboard', ownerOnly: false },
] as const;
type TabKey = (typeof TABS)[number]['key'];

export default function LeakReportingScreen() {
  const clockIso = useSimulationStore((s) => s.clockIso);
  const persona = useSessionStore((s) => s.persona);

  const todayKey = dayKeyOf(clockIso);
  const defaultWeek = useMemo(() => targetWeek(todayKey).days, [todayKey]);
  const [from, setFrom] = useState(defaultWeek[0]!);
  const [to, setTo] = useState(defaultWeek[defaultWeek.length - 1]!);
  const [studioIds, setStudioIds] = useState<StudioId[]>([]);
  // Deep links (the Day sheet's "Money report") open a tab via ?tab=<key>.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>(() => {
    const wanted = searchParams.get('tab');
    return TABS.some((t) => t.key === wanted) ? (wanted as TabKey) : 'summary';
  });

  const days = useMemo(() => daysInRange(from, to), [from, to]);
  const scope = useMemo(() => buildReportingScope(days, studioIds, persona), [days, studioIds, persona]);

  // The finance seats get the Money tab only (W7); everyone else as before.
  const visibleTabs = reportingMoneyTabOnly(persona)
    ? TABS.filter((t) => t.key === 'money')
    : TABS.filter((t) => !t.ownerOnly || scope.moneyVisible);
  const active: TabKey = visibleTabs.some((t) => t.key === tab) ? tab : visibleTabs[0]!.key;

  return (
    <section className="px-8 py-6" data-testid="leak-reporting">
      <header className="max-w-7xl flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="label-small-caps text-gold-bright">Reporting</p>
          <p className="font-serif text-cream-soft mt-1">Leak-first reporting — where the week compounds and where it leaks.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StudioCompareFilter selected={studioIds} onChange={setStudioIds} />
          <ReportDateFilter from={from} to={to} todayIso={clockIso} onChange={(f, t) => { setFrom(f); setTo(t); }} />
        </div>
      </header>

      <nav className="mt-5 flex flex-wrap gap-1 border-b border-gold-dim/20" role="tablist">
        {visibleTabs.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={active === t.key} onClick={() => setTab(t.key)}
            className={clsx('px-3 py-2 text-sm font-inter transition border-b-2 -mb-px',
              active === t.key ? 'text-gold-bright border-gold-bright' : 'text-cream-soft border-transparent hover:text-gold-bright')}>
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-5">
        {/* tv-zits: one tab's bad record shows a message there, never a blank app. */}
        <ReportingTabBoundary key={`${active}|${from}|${to}|${studioIds.join(',')}`} label={visibleTabs.find((t) => t.key === active)?.label ?? 'report'}>
          {active === 'summary' ? <SummaryTab scope={scope} /> : null}
          {active === 'leak' ? <LeakTab scope={scope} /> : null}
          {active === 'targets' ? <TargetsTab scope={scope} /> : null}
          {active === 'rebooks' ? <RebooksTab scope={scope} /> : null}
          {active === 'leads' ? <LeadsAgentsTab scope={scope} /> : null}
          {active === 'customers' ? <CustomersFunnelTab scope={scope} /> : null}
          {active === 'money' ? <MoneyTab scope={scope} /> : null}
          {active === 'owner' ? <OwnerTab scope={scope} /> : null}
          {active === 'leaderboard' ? <LeaderboardTab scope={scope} /> : null}
        </ReportingTabBoundary>
      </div>
    </section>
  );
}
